// src/pages/public/ComparePage.jsx
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { X, ShoppingCart } from 'lucide-react'
import { compareApi, cartApi } from '../../api/services'
import { Button, PageSpinner, Badge } from '../../components/common/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

export default function ComparePage() {
  const [sp, setSp] = useSearchParams()
  const ids = sp.get('ids') || ''
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['compare', ids],
    queryFn: () => compareApi.compare({ ids }),
    enabled: ids.length > 0,
  })

  const addToCart = useMutation({
    mutationFn: (id) => cartApi.add({ product_id: id, quantity: 1 }),
    onSuccess: () => { toast.success('Added to cart'); qc.invalidateQueries(['cart']) },
  })

  const removeFromCompare = (id) => {
    const current = ids.split(',').filter(Boolean)
    const updated = current.filter(i => i !== String(id))
    updated.length ? setSp({ ids: updated.join(',') }) : setSp({})
  }

  const products = data?.data?.data || []

  const ATTRS = [
    ['Category', 'category.name'],
    ['Supplier', 'supplier.name'],
    ['Price/m', p => `₹${Number(p.retail_price || p.price).toLocaleString('en-IN')}`],
    ['Wholesale', p => p.wholesale_price ? `₹${Number(p.wholesale_price).toLocaleString('en-IN')}` : '—'],
    ['GSM', p => p.attributes?.GSM || p.gsm || '—'],
    ['Width', p => p.attributes?.Width || '—'],
    ['Fabric Type', p => p.attributes?.['Fabric Type'] || '—'],
    ['Pattern', p => p.attributes?.Pattern || '—'],
    ['Origin', p => p.attributes?.['Origin'] || '—'],
    ['GI Tagged', p => p.attributes?.['GI Tagged'] || '—'],
    ['Sample', p => p.attributes?.['Sample Available'] || '—'],
    ['Rating', p => p.average_rating ? `${p.average_rating} ★ (${p.reviews_count})` : '—'],
  ]

  if (!ids) return (
    <div className="max-w-4xl mx-auto px-4 py-16 text-center">
      <h1 className="text-2xl font-display font-bold mb-3">Compare Fabrics</h1>
      <p className="text-muted">Add fabrics to compare from the product listing page.</p>
      <Link to="/fabrics" className="mt-4 inline-block"><Button>Browse Fabrics</Button></Link>
    </div>
  )

  if (isLoading) return <PageSpinner />

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 overflow-x-auto">
      <h1 className="text-2xl font-display font-bold text-gray-900 mb-6">Compare Products</h1>

      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-40 text-left p-3 text-xs font-bold text-muted uppercase">Feature</th>
            {products.map(p => (
              <th key={p.id} className="p-3 text-left min-w-48">
                <div className="relative group">
                  <button onClick={() => removeFromCompare(p.id)} className="absolute -top-1 -right-1 p-1 rounded-full bg-gray-100 hover:bg-red-100 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={10} />
                  </button>
                  <div className="w-full aspect-square rounded-xl overflow-hidden bg-surface mb-2">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <Link to={`/fabrics/${p.slug}`} className="text-sm font-bold text-gray-900 hover:text-pink line-clamp-2">{p.name}</Link>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ATTRS.map(([label, accessor]) => (
            <tr key={label} className="border-t border-border even:bg-surface/50">
              <td className="p-3 text-xs font-bold text-muted">{label}</td>
              {products.map(p => (
                <td key={p.id} className="p-3 text-sm text-gray-700">
                  {typeof accessor === 'function' ? accessor(p) : (accessor.includes('.') ? accessor.split('.').reduce((o, k) => o?.[k], p) : p[accessor]) || '—'}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-border">
            <td className="p-3" />
            {products.map(p => (
              <td key={p.id} className="p-3">
                <Button size="sm" loading={addToCart.isPending} onClick={() => addToCart.mutate(p.id)}>
                  <ShoppingCart size={13} /> Add to Cart
                </Button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}
